import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatPhone } from '../utils/formatPhone';

const STATUS_COLORS = {
  new:       { bg: '#1e3a5f', color: '#60a5fa', label: 'New' },
  contacted: { bg: '#1a3a2a', color: '#4ade80', label: 'Contacted' },
  converted: { bg: '#2d1f3d', color: '#c084fc', label: 'Converted' },
  closed:    { bg: '#2a1f1f', color: '#f87171', label: 'Closed' },
};

const LICENSE_COLORS = {
  'AI Agent':   { bg: '#1e3a5f', color: '#60a5fa' },
  'Broadcaster':{ bg: '#2a1f1f', color: '#f87171' },
  'Both':       { bg: '#2d1f3d', color: '#c084fc' },
};

export default function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [noteText, setNoteText]   = useState('');
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const { data } = await api.get(`/api/inquiries${params}`);
      setInquiries(data.inquiries);
      setTotal(data.total);
    } catch {
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id, status) {
    try {
      const { data } = await api.patch(`/api/inquiries/${id}`, { status });
      setInquiries(prev => prev.map(i => i._id === id ? data : i));
      if (selected?._id === id) setSelected(data);
    } catch { alert('Update failed.'); }
  }

  async function saveNote(id) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/inquiries/${id}`, { notes: noteText });
      setInquiries(prev => prev.map(i => i._id === id ? data : i));
      setSelected(data);
    } catch { alert('Save failed.'); }
    setSaving(false);
  }

  async function deleteInquiry(id) {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await api.delete(`/api/inquiries/${id}`);
      setInquiries(prev => prev.filter(i => i._id !== id));
      setTotal(t => t - 1);
      if (selected?._id === id) setSelected(null);
    } catch { alert('Delete failed.'); }
  }

  const counts = inquiries.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});

  return (
    <div style={{ padding: 32, background: '#0f172a', minHeight: '100vh', color: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Website Inquiries</h1>
        <p style={{ fontSize: 14, color: '#64748b' }}>{total} total leads captured from the website</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 28 }}>
        {Object.entries(STATUS_COLORS).map(([key, val]) => (
          <div key={key} style={{ background: '#1e293b', borderRadius: 12, padding: '16px 20px', border: `1px solid ${val.color}20` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: val.color }}>{counts[key] || 0}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{val.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[['', 'All'], ...Object.entries(STATUS_COLORS).map(([k, v]) => [k, v.label])].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding: '7px 18px', borderRadius: 8, border: `1px solid ${filter === key ? '#ef4444' : '#334155'}`,
            background: filter === key ? '#ef444420' : 'transparent',
            color: filter === key ? '#ef4444' : '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>{label}</button>
        ))}
        <button onClick={load} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #334155', background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer', marginLeft: 'auto' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Table + Detail Panel */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* Table */}
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 16, border: '1px solid #334155', overflow: 'hidden', minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>Loading...</div>
          ) : inquiries.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>No inquiries yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Name', 'Company', 'License', 'Size', 'Date', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inquiries.map(inq => {
                  const sc = STATUS_COLORS[inq.status];
                  const lc = LICENSE_COLORS[inq.licenseRequired] || {};
                  const isSelected = selected?._id === inq._id;
                  return (
                    <tr
                      key={inq._id}
                      onClick={() => { setSelected(inq); setNoteText(inq.notes || ''); }}
                      style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer', background: isSelected ? '#263548' : 'transparent', transition: 'background 0.15s' }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#243040'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{inq.name}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{formatPhone(inq.phone)}</div>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ fontSize: 13, color: '#e2e8f0' }}>{inq.companyName}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{inq.companyEmail}</div>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: lc.bg, color: lc.color }}>{inq.licenseRequired}</span>
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13, color: '#94a3b8' }}>{inq.companySize}</td>
                      <td style={{ padding: '13px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {new Date(inq.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc?.bg, color: sc?.color }}>{sc?.label}</span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <button onClick={e => { e.stopPropagation(); deleteInquiry(inq._id); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, opacity: 0.6, lineHeight: 1 }} title="Delete">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div style={{ width: 320, flexShrink: 0, background: '#1e293b', borderRadius: 16, border: '1px solid #334155', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>Detail</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {[
                ['Name', selected.name],
                ['Phone', formatPhone(selected.phone)],
                ['Designation', selected.designation || '—'],
                ['Email', selected.companyEmail],
                ['Company', selected.companyName],
                ['Size', selected.companySize],
                ['License', selected.licenseRequired],
                ['Submitted', new Date(selected.createdAt).toLocaleString('en-IN')],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, color: '#e2e8f0', wordBreak: 'break-all' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Status update */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Update Status</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_COLORS).map(([key, val]) => (
                  <button key={key} onClick={() => updateStatus(selected._id, key)} style={{
                    padding: '5px 12px', borderRadius: 8, border: `1px solid ${selected.status === key ? val.color : '#334155'}`,
                    background: selected.status === key ? val.bg : 'transparent',
                    color: selected.status === key ? val.color : '#64748b',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>{val.label}</button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Notes</div>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={4}
                placeholder="Add notes about this lead..."
                style={{
                  width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
                  color: '#f1f5f9', fontSize: 13, padding: '10px 12px', resize: 'vertical', outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              <button onClick={() => saveNote(selected._id)} disabled={saving} style={{
                marginTop: 8, width: '100%', padding: '9px', borderRadius: 8, border: 'none',
                background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                opacity: saving ? 0.7 : 1,
              }}>{saving ? 'Saving…' : 'Save Notes'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
